import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { AbstractControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DatetimeInputComponent } from '../datetime-input.component';
import { distinctUntilChanged, takeUntil } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { FormlyErrorComponent } from '../formly-error.component';
import { DateHelperService } from '../../services/date-helper.service';
import { ClearFieldButtonComponent } from './clear-field-button.component';
import { ClearableFieldType } from './clearable-field.type';
import { commitUserValue } from './user-edit.util';

@Component({
  selector: 'formly-field-datetime',
  template: `
    <div class="mb-1">
      <div class="dd-clear-field">
        <app-datetime-input
          #datetimeInput
          class="block w-full"
          [label]="props.label"
          [required]="props.required"
          [isDisabled]="props.disabled"
          [dateTime]="field.formControl.value"
          [hideDate]="props.hideDate || props.showOnlyTime"
          [hideTimelabel]="props.hideTimelabel"
          [info]="props.info"
          [formlyDataCy]="field"
          (dateTimeChange)="onDateTimeChange($event)">
          <app-clear-field-button
            datetimeTrailingAction
            [field]="field"
            [hasValue]="hasClearableValue()"
            [disabled]="isDisabled()"
            [readOnly]="isReadOnly()"
            [ariaLabel]="clearAriaLabel()"
            [focusTarget]="datetimeInput"
            (clearSelection)="clear()" />
        </app-datetime-input>
      </div>
      @if (field.props.description) {
        <mat-hint class="inline-block mb-2">{{ field.props.description }}</mat-hint>
      }
      <app-formly-error [field]="field"></app-formly-error>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    DatetimeInputComponent,
    MatInputModule,
    MatDatepickerModule,
    FormlyDataCyDirective,
    FormlyErrorComponent,
    ClearFieldButtonComponent,
  ],
})
export class FormlyFieldDateTime extends ClearableFieldType implements OnInit {
  cd = inject(ChangeDetectorRef);
  destroy$ = injectDestroy();
  constructor() {
    super();
  }

  /**
   * The inner datetime input is not a ControlValueAccessor for this field's control — it emits a
   * value we write ourselves — so nothing marks the control dirty unless we do (#21026). This also
   * covers `date_range`/`week`, whose start/end children are datetime fields.
   */
  onDateTimeChange(value: unknown): void {
    commitUserValue(this.formControl, value);
  }

  ngOnInit() {
    // if (this.props?.useCurrentDate && !this.field.formControl.value) {
    //   this.field.formControl.setValue(DateHelperService.roundHours(new Date()));
    // }

    // Time-only fields (showOnlyTime/hideDate) keep a full datetime value, so a
    // prefilled date — or leftover value after the time is cleared — satisfies
    // Formly's value-based `required` validator even when no time is entered. The
    // form then looks valid and the save reaches the backend with a missing time,
    // which crashes server-side. Enforce that a time component is actually present.
    if (this.props?.showOnlyTime || this.props?.hideDate) {
      this.field.formControl.addValidators(this.#requiredTimeValidator);
      this.field.formControl.updateValueAndValidity({ emitEvent: false });
    }

    this.field.formControl.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe(() => {
      this.cd.markForCheck();
    });
  }

  readonly #requiredTimeValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!this.props?.required || this.props?.disabled) return null;
    const { time } = DateHelperService.dateTimeToDateAndTime(control.value ?? null);
    return time ? null : { required: true };
  };
}
